import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import { ProviderSearchDialog } from "@/components/ProviderSearchDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Send } from "lucide-react";

export default function Messages() {
  const { t } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [isMaster, setIsMaster] = useState(false);
  const endRef = useRef(null);

  const loadConvs = () => api.get("/chat").then((r) => setConvs(r.data)).catch(() => {});
  const fmtTime = (iso) => {
    try {
      const d = new Date(iso);
      const now = new Date();
      return d.toDateString() === now.toDateString()
        ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        : d.toLocaleDateString();
    } catch { return ""; }
  };
  useEffect(() => {
    loadConvs();
    api.get("/businesses/mine").then((r) => setIsMaster(!!r.data?.is_master)).catch(() => {});
    const iv = setInterval(loadConvs, 8000);
    return () => clearInterval(iv);
  }, []);

  const selectConv = (id) => {
    setActive(id);
    setConvs((prev) => prev.map((c) => (c.id === id ? { ...c, biz_unread: 0 } : c)));
    api.post(`/chat/${id}/read`).catch(() => {});
    window.dispatchEvent(new Event("chat-read"));
  };

  // Auto-open a conversation passed via ?open=<sid> (e.g. from "Abrir chat")
  useEffect(() => {
    const open = searchParams.get("open");
    if (open) { selectConv(open); loadConvs(); setSearchParams({}, { replace: true }); }
  }, []);

  useEffect(() => {
    if (!active) return;
    const load = () => api.get(`/chat/${active}/messages`).then((r) => setMsgs(r.data.messages)).catch(() => {});
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [active]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text.trim() || !active) return;
    const t2 = text; setText("");
    try {
      await api.post(`/chat/${active}/business-message`, { text: t2 });
      api.get(`/chat/${active}/messages`).then((r) => setMsgs(r.data.messages)).catch(() => {});
      loadConvs();
    } catch (e) {
      setText(t2);
      toast.error(t("chat_send_error"));
    }
  };

  return (
    <div data-testid="messages-page">
      <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-secondary"><MessageSquare className="h-6 w-6 text-primary" /> {t("dash_messages")}</h1>
      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="space-y-2 md:col-span-1">
          {convs.map((c) => (
            <button key={c.id} onClick={() => selectConv(c.id)} data-testid={`conv-${c.id}`} className={`flex w-full items-center justify-between gap-2 rounded-sm border p-3 text-left ${active === c.id ? "border-primary bg-primary/5" : "border-slate-200 bg-white"}`}>
              <div className="min-w-0"><p className="truncate font-semibold text-secondary">{c.client_name}</p><p className="truncate text-xs text-slate-400">{c.client_email || c.city || ""}</p></div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                {c.last_at && <span className="text-[10px] text-slate-400">{fmtTime(c.last_at)}</span>}
                {c.biz_unread > 0 && <span className="rounded-full bg-red-500 px-2 text-xs font-bold text-white">{c.biz_unread}</span>}
              </div>
            </button>
          ))}
          {convs.length === 0 && <p className="rounded-sm border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">{t("chat_no_convs")}</p>}
        </div>
        <div className="flex min-h-[420px] flex-col rounded-sm border border-slate-200 bg-white md:col-span-2">
          {active ? (
            <>
              {(() => { const ac = convs.find((c) => c.id === active); return (
                <div className="flex items-center justify-between gap-2 border-b border-slate-200 p-3" data-testid="chat-active-header">
                  <div>
                    <p className="font-semibold text-secondary">{ac?.client_name}</p>
                    <p className="text-xs text-slate-400">{ac?.city ? ac.city : ""}{ac?.client_email ? ` · ${ac.client_email}` : ""}</p>
                  </div>
                  {isMaster && <ProviderSearchDialog defaultCity={ac?.city || ""} defaultCategory="" contextId={ac?.id || ""} clientName={ac?.client_name || ""} clientEmail={ac?.client_email || ""} testId="chat-providers" />}
                </div>
              ); })()}
              <div className="flex-1 space-y-2 overflow-y-auto p-4" data-testid="chat-thread">
                {msgs.map((m, i) => (
                  <div key={m.id || i} className={`w-fit max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.sender === "business" ? "ml-auto bg-primary text-white" : "bg-slate-100 text-slate-800"}`}>{m.text}</div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="flex gap-2 border-t border-slate-200 p-3">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t("chat_reply_ph")} className="rounded-sm" data-testid="chat-input" />
                <Button onClick={send} className="rounded-sm bg-primary hover:bg-orange-600" data-testid="chat-send-btn"><Send className="h-4 w-4" /></Button>
              </div>
            </>
          ) : <div className="flex flex-1 items-center justify-center text-slate-400">{t("chat_select")}</div>}
        </div>
      </div>
    </div>
  );
}
